import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSnackbarContext } from "components/snackbar";
import { EditorRef } from "react-email-editor";
import {
  ICampaign,
  CampaignType,
  CampaignFilters,
  DEFAULT_FILTERS,
  RecipientCountResponse,
  SelectedCustomer,
} from "../../types/ICampaign";
import { IMergeField } from "views/outreach/templates/types/IEmailTemplate";
import {
  getCampaign,
  createCampaign,
  updateCampaign,
  sendCampaign,
  scheduleCampaign,
  cancelCampaignSchedule,
  getRecipientCount,
  getTemplates,
  getCustomersByIds,
} from "../../api/campaigns";
import { getMergeFields, getTemplate } from "views/outreach/templates/api/templates";

interface CampaignEditorErrors {
  name?: string;
  subject?: string;
  messageText?: string;
}

export const useCampaignEditor = (
  emailEditorRef: React.RefObject<EditorRef>
) => {
  const { id } = useParams<{ id?: string }>();
  const history = useHistory();
  const location = useLocation();
  const queryClient = useQueryClient();
  const snackBar = useSnackbarContext();

  const isEditing = Boolean(id);

  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [campaignType, setCampaignTypeState] = useState<CampaignType>("email");
  const [messageText, setMessageText] = useState("");
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [filters, setFilters] = useState<CampaignFilters>(DEFAULT_FILTERS);
  const [touched, setTouched] = useState({
    name: false,
    subject: false,
    messageText: false,
  });
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState<SelectedCustomer[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showSendConfirm, setShowSendConfirm] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [isScheduling, setIsScheduling] = useState(false);
  const [showSaveFirstModal, setShowSaveFirstModal] = useState(false);
  const [editorReady, setEditorReady] = useState(false);

  // Open filters modal automatically when redirected after save-first flow
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openFilters") === "true" && id) {
      setShowFilterModal(true);
      // Clean up the query param
      history.replace(`/outreach/campaigns/${id}/edit`);
    }
  }, [location.search, id, history]);

  // Only allow type change when creating a new campaign
  const setCampaignType = useCallback(
    (type: CampaignType) => {
      if (!isEditing) {
        setCampaignTypeState(type);
      }
    },
    [isEditing]
  );

  // Debounce timer for recipient count
  const countTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedFilters, setDebouncedFilters] =
    useState<CampaignFilters>(filters);

  useEffect(() => {
    if (countTimerRef.current) {
      clearTimeout(countTimerRef.current);
    }
    countTimerRef.current = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 500);
    return () => {
      if (countTimerRef.current) {
        clearTimeout(countTimerRef.current);
      }
    };
  }, [filters]);

  const errors: CampaignEditorErrors = useMemo(() => {
    const errs: CampaignEditorErrors = {};
    if (touched.name && !name.trim()) {
      errs.name = "Campaign name is required";
    }
    if (campaignType === "email" && touched.subject && !subject.trim()) {
      errs.subject = "Subject line is required";
    }
    if (campaignType === "sms" && touched.messageText && !messageText.trim()) {
      errs.messageText = "Message text is required";
    }
    return errs;
  }, [name, subject, messageText, campaignType, touched]);

  // Load existing campaign
  const { data: campaign, isLoading: isLoadingCampaign } =
    useQuery<ICampaign>(["campaign", id], () => getCampaign(id!), {
      enabled: isEditing,
      onSuccess: async (data) => {
        setName(data.name);
        setSubject(data.subject || "");
        setCampaignTypeState(data.campaignType || "email");
        setMessageText(data.messageText || "");
        setTemplateId(data.templateId);
        setFilters(data.filters || DEFAULT_FILTERS);

        // Resolve selected customer IDs to full objects for the picker
        const customerIds = data.filters?.includeCustomerIds;
        if (customerIds && customerIds.length > 0) {
          try {
            const customers = await getCustomersByIds(customerIds);
            setSelectedCustomers(customers);
          } catch {
            // Silently fail — customers won't show in picker but IDs are still in filters
          }
        }
      },
    });

  // Load templates for dropdown
  const { data: allTemplates = [], isLoading: isLoadingTemplates } = useQuery<
    { id: string; name: string; subject: string; templateType: string; messageText: string | null }[]
  >("campaign-templates", getTemplates);

  // Filter templates by campaign type
  const templates = allTemplates.filter(
    (tpl) => tpl.templateType === campaignType
  );

  // Load merge fields for Unlayer / SMS
  const { data: mergeFields = [], isLoading: isLoadingMergeFields } =
    useQuery<IMergeField[]>(
      ["merge-fields", campaignType],
      () => getMergeFields(campaignType === "sms" ? "sms" : undefined),
    );

  const mergeTags = useMemo(() => {
    return mergeFields.reduce<
      Record<string, { name: string; value: string }>
    >(
      (acc, field) => ({
        ...acc,
        [field.key]: { name: field.label, value: `{{${field.key}}}` },
      }),
      {}
    );
  }, [mergeFields]);

  // Recipient count - uses a saved campaign ID
  const {
    data: recipientCountData,
    isLoading: isCountLoading,
    refetch: refetchCount,
  } = useQuery<RecipientCountResponse>(
    ["campaign-recipient-count", id],
    () => getRecipientCount(id!),
    {
      enabled: Boolean(id),
    }
  );

  const recipientCount = recipientCountData?.count ?? null;

  // Create campaign mutation
  const createMutation = useMutation(
    (data: {
      campaignType?: CampaignType;
      name: string;
      subject?: string;
      templateId?: string | null;
      body?: string | null;
      designJson?: Record<string, unknown> | null;
      messageText?: string | null;
      filters?: CampaignFilters;
    }) => createCampaign(data),
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries("campaigns");
        snackBar.showSnackbar("Campaign saved as draft");
        setIsDirty(false);
        history.push(`/outreach/campaigns/${data.id}/edit`);
      },
      onError: () => {
        snackBar.showSnackbar("Failed to create campaign");
      },
      onSettled: () => {
        setIsSaving(false);
      },
    }
  );

  // Update campaign mutation
  const updateMutation = useMutation(
    (data: {
      id: string;
      payload: Partial<{
        name: string;
        subject: string;
        templateId: string | null;
        body: string | null;
        designJson: Record<string, unknown> | null;
        messageText: string | null;
        filters: CampaignFilters;
      }>;
    }) => updateCampaign(data.id, data.payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        queryClient.invalidateQueries(["campaign", id]);
        snackBar.showSnackbar("Campaign updated");
        setIsDirty(false);
      },
      onError: () => {
        snackBar.showSnackbar("Failed to update campaign");
      },
      onSettled: () => {
        setIsSaving(false);
      },
    }
  );

  // Send campaign mutation
  const sendMutation = useMutation(
    (campaignId: string) => sendCampaign(campaignId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("campaigns");
        queryClient.invalidateQueries(["campaign", id]);
        snackBar.showSnackbar("Campaign is being sent!");
        setShowSendConfirm(false);
        history.push("/outreach/campaigns");
      },
      onError: () => {
        snackBar.showSnackbar("Failed to send campaign");
        setShowSendConfirm(false);
      },
    }
  );

  const extractEditorContent = useCallback((): Promise<{
    design: Record<string, unknown>;
    html: string;
  }> => {
    return new Promise((resolve, reject) => {
      if (!emailEditorRef.current?.editor) {
        reject(new Error("Editor not ready"));
        return;
      }
      emailEditorRef.current.editor.exportHtml((data) => {
        resolve({
          design: data.design as unknown as Record<string, unknown>,
          html: data.html,
        });
      });
    });
  }, [emailEditorRef]);

  const handleSave = useCallback(async () => {
    setTouched({ name: true, subject: true, messageText: true });

    if (!name.trim()) {
      return;
    }

    if (campaignType === "email" && !subject.trim()) {
      return;
    }

    if (campaignType === "sms" && !messageText.trim()) {
      return;
    }

    if (campaignType === "sms") {
      setIsSaving(true);
      if (isEditing && id) {
        updateMutation.mutate({
          id,
          payload: {
            name: name.trim(),
            messageText: messageText.trim(),
            filters,
          },
        });
      } else {
        createMutation.mutate({
          campaignType: "sms",
          name: name.trim(),
          messageText: messageText.trim(),
          filters,
        });
      }
      return;
    }

    // Email path
    if (!emailEditorRef.current?.editor) {
      snackBar.showSnackbar("Editor is not ready yet");
      return;
    }

    setIsSaving(true);

    try {
      const { design, html } = await extractEditorContent();

      if (isEditing && id) {
        updateMutation.mutate({
          id,
          payload: {
            name: name.trim(),
            subject: subject.trim(),
            templateId,
            body: html,
            designJson: design,
            filters,
          },
        });
      } else {
        createMutation.mutate({
          campaignType: "email",
          name: name.trim(),
          subject: subject.trim(),
          templateId,
          body: html,
          designJson: design,
          filters,
        });
      }
    } catch {
      snackBar.showSnackbar("Failed to export editor content");
      setIsSaving(false);
    }
  }, [
    name,
    subject,
    campaignType,
    messageText,
    templateId,
    filters,
    isEditing,
    id,
    emailEditorRef,
    extractEditorContent,
    createMutation,
    updateMutation,
    snackBar,
  ]);

  const handleSendNow = useCallback(async () => {
    setTouched({ name: true, subject: true, messageText: true });

    if (!name.trim()) {
      return;
    }

    if (campaignType === "email" && !subject.trim()) {
      return;
    }

    if (campaignType === "sms" && !messageText.trim()) {
      return;
    }

    // SMS path
    if (campaignType === "sms") {
      if (!id) {
        setIsSaving(true);
        try {
          const created = await createCampaign({
            campaignType: "sms",
            name: name.trim(),
            messageText: messageText.trim(),
            filters,
          });
          queryClient.invalidateQueries("campaigns");
          history.push(`/outreach/campaigns/${created.id}/edit`);
          snackBar.showSnackbar("Campaign saved. You can now send it.");
        } catch {
          snackBar.showSnackbar("Failed to save campaign before sending");
        } finally {
          setIsSaving(false);
        }
        return;
      }

      setIsSaving(true);
      try {
        await updateCampaign(id, {
          name: name.trim(),
          messageText: messageText.trim(),
          filters,
        });
        queryClient.invalidateQueries(["campaign", id]);
        setShowSendConfirm(true);
      } catch {
        snackBar.showSnackbar("Failed to save campaign before sending");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Email path
    if (!emailEditorRef.current?.editor) {
      snackBar.showSnackbar("Editor is not ready yet");
      return;
    }

    // If campaign is not yet saved, save first then show confirmation
    if (!id) {
      setIsSaving(true);
      try {
        const { design, html } = await extractEditorContent();
        const created = await createCampaign({
          campaignType: "email",
          name: name.trim(),
          subject: subject.trim(),
          templateId,
          body: html,
          designJson: design,
          filters,
        });
        queryClient.invalidateQueries("campaigns");
        history.push(`/outreach/campaigns/${created.id}/edit`);
        snackBar.showSnackbar("Campaign saved. You can now send it.");
      } catch {
        snackBar.showSnackbar("Failed to save campaign before sending");
      } finally {
        setIsSaving(false);
      }
      return;
    }

    // Save current content before showing send confirmation
    setIsSaving(true);
    try {
      const { design, html } = await extractEditorContent();
      await updateCampaign(id, {
        name: name.trim(),
        subject: subject.trim(),
        templateId,
        body: html,
        designJson: design,
        filters,
      });
      queryClient.invalidateQueries(["campaign", id]);
      setShowSendConfirm(true);
    } catch {
      snackBar.showSnackbar("Failed to save campaign before sending");
    } finally {
      setIsSaving(false);
    }
  }, [
    name,
    subject,
    campaignType,
    messageText,
    templateId,
    filters,
    id,
    emailEditorRef,
    extractEditorContent,
    queryClient,
    history,
    snackBar,
  ]);

  const confirmSend = useCallback(() => {
    if (id) {
      sendMutation.mutate(id);
    }
  }, [id, sendMutation]);

  const handleScheduleClick = useCallback(() => {
    if (!isEditing) {
      setShowSaveFirstModal(true);
      return;
    }
    setShowScheduleModal(true);
  }, [isEditing]);

  const confirmSchedule = useCallback(
    async (scheduledAt: string, timezone: string) => {
      if (!id) {
        return;
      }
      setIsScheduling(true);
      try {
        await scheduleCampaign(id, scheduledAt, timezone);
        queryClient.invalidateQueries("campaigns");
        queryClient.invalidateQueries(["campaign", id]);
        snackBar.showSnackbar("Campaign scheduled");
        setShowScheduleModal(false);
        history.push("/outreach/campaigns");
      } catch {
        snackBar.showSnackbar("Failed to schedule campaign");
      } finally {
        setIsScheduling(false);
      }
    },
    [id, queryClient, snackBar, history]
  );

  const handleCancelSchedule = useCallback(async () => {
    if (!id) {
      return;
    }
    try {
      await cancelCampaignSchedule(id);
      queryClient.invalidateQueries("campaigns");
      queryClient.invalidateQueries(["campaign", id]);
      snackBar.showSnackbar("Schedule cancelled");
    } catch {
      snackBar.showSnackbar("Failed to cancel schedule");
    }
  }, [id, queryClient, snackBar]);

  const handleTemplateChange = useCallback(
    async (newTemplateId: string | null) => {
      setTemplateId(newTemplateId);

      if (!newTemplateId) {
        return;
      }

      try {
        if (campaignType === "sms") {
          // For SMS: load messageText from the template
          const tpl = templates.find((t) => t.id === newTemplateId);
          if (tpl?.messageText) {
            setMessageText(tpl.messageText);
          }
        } else {
          // For email: load design into Unlayer editor
          if (!emailEditorRef.current?.editor) {
            return;
          }
          const fullTemplate = await getTemplate(newTemplateId);
          if (fullTemplate.designJson && Object.keys(fullTemplate.designJson).length > 0) {
            emailEditorRef.current.editor.loadDesign(
              fullTemplate.designJson as Parameters<
                NonNullable<typeof emailEditorRef.current>["editor"]["loadDesign"]
              >[0]
            );
          }
          if (fullTemplate.subject && !subject.trim()) {
            setSubject(fullTemplate.subject);
          }
        }
      } catch {
        snackBar.showSnackbar("Failed to load template");
      }
    },
    [emailEditorRef, subject, campaignType, templates, setMessageText, snackBar]
  );

  // When "Filter Recipients" is clicked on an unsaved campaign, show save-first modal
  const handleFilterRecipientsClick = useCallback(() => {
    if (!id) {
      setShowSaveFirstModal(true);
    } else {
      setShowFilterModal(true);
    }
  }, [id]);

  // Save the campaign then open filters modal
  const handleSaveFirstConfirm = useCallback(async () => {
    setShowSaveFirstModal(false);

    if (!name.trim()) {
      setTouched({ name: true, subject: true, messageText: true });
      return;
    }

    setIsSaving(true);
    try {
      let payload: Parameters<typeof createCampaign>[0];

      if (campaignType === "sms") {
        payload = {
          campaignType: "sms",
          name: name.trim(),
          messageText: messageText.trim(),
          filters,
        };
      } else {
        const { design, html } = emailEditorRef.current?.editor
          ? await extractEditorContent()
          : { design: null, html: null };
        payload = {
          campaignType: "email",
          name: name.trim(),
          subject: subject.trim(),
          templateId,
          body: html,
          designJson: design as Record<string, unknown> | null,
          filters,
        };
      }

      const created = await createCampaign(payload);
      queryClient.invalidateQueries("campaigns");
      snackBar.showSnackbar("Campaign saved as draft");
      history.push(`/outreach/campaigns/${created.id}/edit?openFilters=true`);
    } catch {
      snackBar.showSnackbar("Failed to save campaign");
    } finally {
      setIsSaving(false);
    }
  }, [name, subject, campaignType, messageText, templateId, filters, emailEditorRef, extractEditorContent, queryClient, history, snackBar]);

  const handleApplyFilters = useCallback(
    (newFilters: CampaignFilters, customers: SelectedCustomer[]) => {
      setFilters(newFilters);
      setSelectedCustomers(customers);
      setShowFilterModal(false);

      // If campaign is saved, update filters on backend and refresh count
      if (id) {
        updateCampaign(id, { filters: newFilters })
          .then(() => {
            queryClient.invalidateQueries([
              "campaign-recipient-count",
              id,
            ]);
          })
          .catch(() => {
            snackBar.showSnackbar("Failed to save filters. Please try again.");
          });
      }
    },
    [id, queryClient]
  );

  const isLoading =
    isLoadingCampaign || isLoadingTemplates || isLoadingMergeFields;

  const markDirty = <T,>(setter: (v: T) => void) => (v: T) => {
    setter(v);
    setIsDirty(true);
  };

  return {
    // State
    name,
    setName: markDirty(setName),
    subject,
    setSubject: markDirty(setSubject),
    campaignType,
    setCampaignType: markDirty(setCampaignTypeState),
    messageText,
    setMessageText: markDirty(setMessageText),
    templateId,
    filters,
    selectedCustomers,
    isEditing,
    campaign,
    templates,
    mergeFields,
    mergeTags,
    recipientCount,
    isCountLoading,
    editorReady,
    setEditorReady,

    // UI state
    showFilterModal,
    setShowFilterModal,
    showSendConfirm,
    setShowSendConfirm,
    showScheduleModal,
    setShowScheduleModal,
    isScheduling,
    showSaveFirstModal,
    setShowSaveFirstModal,

    // Derived state
    isLoading,
    isSaving,
    isDirty,
    errors,
    touched,
    setTouched,
    isSending: sendMutation.isLoading,

    // Handlers
    handleSave,
    handleSendNow,
    handleScheduleClick,
    confirmSchedule,
    handleCancelSchedule,
    confirmSend,
    handleTemplateChange,
    handleApplyFilters,
    handleFilterRecipientsClick,
    handleSaveFirstConfirm,
  };
};
